import { BrowserOAuthClient, buildLoopbackClientId, atprotoLoopbackClientMetadata } from '@atproto/oauth-client-browser';
import { COLLECTION, RKEY, OAUTH_SCOPE, MIN_COMMON } from '../shared/config.js';
import { QUESTIONS, compatibility, explain } from '../shared/scoring.js';

const $ = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const show = (id) => { ['intro', 'quiz', 'saving', 'results', 'all'].forEach((s) => $(s).classList.toggle('hidden', s !== id)); window.scrollTo({ top: 0 }); };
const pct = (s) => (s == null ? '—' : Math.round(s * 100) + '%');
const label = (p) => (p.seed ? p.name : p.displayName || '@' + p.handle);
const api = async (path, init) => {
  const r = await fetch(path, { ...init, headers: { 'content-type': 'application/json', ...(init?.headers || {}) } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `${r.status}`);
  return data;
};

// ---------- OAuth ----------
const LOOPBACK = location.hostname === '127.0.0.1' || location.hostname === '[::1]' || location.hostname === 'localhost';
// Local dev uses the special loopback client id (no hosted metadata needed); production loads ours.
const client = LOOPBACK
  ? new BrowserOAuthClient({
      handleResolver: 'https://bsky.social',
      clientMetadata: atprotoLoopbackClientMetadata(`${buildLoopbackClientId(location)}&scope=${encodeURIComponent(OAUTH_SCOPE)}`),
    })
  : await BrowserOAuthClient.load({ clientId: `${location.origin}/client-metadata.json`, handleResolver: 'https://bsky.social' });

let session = null, me = { did: null, handle: '', answers: {} }, cur = 0, lastResults = null;

// ---------- question order ----------
// Shuffled per person, but not freely: everyone gets the CORE questions first, so any two
// people who bail after a dozen still overlap enough to score. Spicy ones come last.
// The shuffle is seeded from the viewer's DID, so the order is stable across reloads and devices.
const mulberry32 = (a) => () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
const seedOf = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };
function shuffled(list, rnd) { const a = [...list]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
let DECK = QUESTIONS;
let includeSpicy = true;
try { includeSpicy = localStorage.getItem('skymate.spicy') !== 'off'; } catch {}
const SPICY_COUNT = QUESTIONS.filter((q) => q.spicy).length;

function buildDeck(seed) {
  const rnd = mulberry32(seedOf(seed || 'anon'));
  const core = QUESTIONS.filter((q) => q.core);
  const spicy = QUESTIONS.filter((q) => q.spicy && !q.core);
  const rest = QUESTIONS.filter((q) => !q.core && !q.spicy);
  DECK = [...shuffled(core, rnd), ...shuffled(rest, rnd), ...(includeSpicy ? shuffled(spicy, rnd) : [])];
}

/** Turning the toggle off also drops any spicy answers already given, so they never
 *  reach the public record. Turning it back on re-deals them at the end of the deck. */
function setSpicy(on) {
  includeSpicy = on;
  try { localStorage.setItem('skymate.spicy', on ? 'on' : 'off'); } catch {}
  $('spicyIntro').checked = on; $('spicyQuiz').checked = on;
  if (!on) { for (const q of QUESTIONS) if (q.spicy) delete me.answers[q.id]; saveLocal(); }
  const here = DECK[cur]?.id;
  buildDeck(me.did);
  if (!$('quiz').classList.contains('hidden')) {
    const i = DECK.findIndex((q) => q.id === here);
    cur = i >= 0 ? i : Math.min(cur, DECK.length - 1);
    renderQ();
  }
}
$('spicyIntro').onchange = (e) => setSpicy(e.target.checked);
$('spicyQuiz').onchange = (e) => setSpicy(e.target.checked);

function loadLocal() { try { const s = JSON.parse(localStorage.getItem('skymate.answers') || 'null'); if (s && s.did === me.did) me.answers = s.answers || {}; } catch {} }
function saveLocal() { try { localStorage.setItem('skymate.answers', JSON.stringify({ did: me.did, answers: me.answers })); } catch {} }

async function boot() {
  renderIntro();
  loadPool();
  let result = null;
  try { result = await client.init(); } catch (e) { showLoginError(e); }
  if (result?.session) {
    session = result.session;
    me.did = session.did; loadLocal(); buildDeck(me.did);
    try {
      const r = await fetch(`https://public.api.bsky.app/xrpc/com.atproto.repo.describeRepo?repo=${encodeURIComponent(me.did)}`);
      me.handle = (await r.json()).handle || me.did;
    } catch { me.handle = me.did; }
    renderWho();
    // Already synced before? Show results straight away; otherwise start the quiz.
    try { lastResults = await api(`/api/match/${encodeURIComponent(me.did)}`); renderResults(); show('results'); }
    catch { cur = 0; show('quiz'); renderQ(); }
  } else {
    renderWho();
  }
}

function renderIntro() {
  $('lede').innerHTML = `${QUESTIONS.length} questions of no consequence. One <em>soulmate</em>. One nemesis.`;
  $('spicyCountIntro').textContent = `· ${SPICY_COUNT} of them, saved publicly like the rest`;
  $('spicyIntro').checked = includeSpicy; $('spicyQuiz').checked = includeSpicy;
}
function renderWho() {
  $('whobar').innerHTML = session ? `<span>signed in as <b>@${esc(me.handle)}</b></span>` : '';
}
function showLoginError(e) {
  const el = $('loginErr'); el.classList.remove('hidden');
  el.textContent = `Couldn't sign in: ${e.message || e}`;
}

$('loginForm').onsubmit = async (e) => {
  e.preventDefault();
  const h = $('handle').value.trim().replace(/^@/, '').toLowerCase();
  if (!h) return;
  $('loginBtn').disabled = true; $('loginBtn').textContent = 'Opening Bluesky…';
  try { await client.signInRedirect(h.includes('.') ? h : `${h}.bsky.social`, { scope: OAUTH_SCOPE }); }
  catch (err) { showLoginError(err); $('loginBtn').disabled = false; $('loginBtn').textContent = 'Sign in with Bluesky'; }
};
$('logout').onclick = async () => { try { await session?.signOut(); } catch {} location.href = '/'; };

// ---------- pool ----------
async function loadPool() {
  try {
    const d = await api('/api/pool');
    const c = $('chips'); c.innerHTML = '';
    const shown = [...d.profiles].sort(() => Math.random() - 0.5).slice(0, 12);
    for (const p of shown) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'chip tap'; b.textContent = label(p);
      b.onclick = () => { $('lookup').value = p.seed ? p.name : p.handle; runLookup('lookup', 'lookupSlot'); };
      c.appendChild(b);
    }
    const more = document.createElement('span'); more.className = 'chip';
    more.textContent = `+ ${Math.max(0, d.count - shown.length)} more · ${d.people} real ${d.people === 1 ? 'person' : 'people'}, ${d.count - d.people} ringers`;
    c.appendChild(more);
  } catch (e) { $('chips').innerHTML = `<span class="chip">Couldn't load the pool.</span>`; }
}

// ---------- quiz ----------
const answeredCount = () => DECK.filter((q) => me.answers[q.id]).length;
const FINISH_AT = 12; // the button appears once there's enough to say something interesting
function renderQ() {
  const q = DECK[cur], a = me.answers[q.id];
  $('qnum').textContent = `Question ${cur + 1} of ${DECK.length}`;
  $('prompt').textContent = q.prompt;
  const o = $('opts'); o.innerHTML = '';
  q.options.forEach((t, i) => {
    const b = document.createElement('button'); b.type = 'button'; b.className = 'opt' + (a && a.choice === i ? ' picked' : '');
    b.innerHTML = `<span class="k">${String.fromCharCode(65 + i)}</span><span></span>`; b.lastChild.textContent = t;
    b.onclick = () => { me.answers[q.id] = { choice: i }; saveLocal(); renderQ(); setTimeout(next, 140); };
    o.appendChild(b);
  });
  $('back').disabled = cur === 0;
  $('skip').textContent = cur === DECK.length - 1 ? (a ? 'Finish →' : 'Skip & finish →') : a ? 'Next →' : 'Skip →';
  const p = $('progress'); p.innerHTML = '';
  DECK.forEach((qq, i) => { const s = document.createElement('i'); s.className = i === cur ? 'cur' : me.answers[qq.id] ? 'done' : ''; p.appendChild(s); });
  const n2 = answeredCount(), fin = $('finishNow');
  $('answered').textContent = `${n2} answered`;
  fin.classList.toggle('hidden', n2 < FINISH_AT || cur === DECK.length - 1);
  fin.textContent = `Show my matches · ${n2} answered`;
}
function next() { if (cur < DECK.length - 1) { cur++; renderQ(); } else finish(); }
$('back').onclick = () => { if (cur > 0) { cur--; renderQ(); } };
$('skip').onclick = next;
$('finishNow').onclick = finish;
$('redo').onclick = () => { cur = 0; show('quiz'); renderQ(); };

async function finish() {
  const answered = Object.keys(me.answers).length;
  const err = $('quizErr');
  if (answered < MIN_COMMON) { err.classList.remove('hidden'); err.textContent = `Answer at least ${MIN_COMMON} questions to get matched — you've done ${answered}.`; return; }
  err.classList.add('hidden');
  show('saving');
  try {
    $('savingStep').textContent = 'Saving the record to your repo…';
    const put = await session.fetchHandler('/xrpc/com.atproto.repo.putRecord', {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        repo: me.did, collection: COLLECTION, rkey: RKEY, validate: false,
        record: { $type: COLLECTION, version: 1, answers: me.answers, public: true, createdAt: new Date().toISOString() },
      }),
    });
    if (!put.ok) throw new Error((await put.json().catch(() => ({}))).message || `PDS returned ${put.status}`);
    $('savingStep').textContent = 'Reading it back from your server and scoring…';
    lastResults = await api('/api/sync', { method: 'POST', body: JSON.stringify({ did: me.did }) });
    renderResults(); show('results');
  } catch (e) {
    show('quiz'); err.classList.remove('hidden'); err.textContent = `Couldn't save: ${e.message || e}`;
  }
}

// ---------- results ----------
function phrase(e, who) { return phraseBetween(e, 'you', who); }

/** Option text drops its leading capital mid-sentence, but only when that's safe:
 *  never for "I've read a few", never for "NSYNC" or "Backstreet Boys". */
const lc = (s) => (/^I\b|^I'/.test(s) || /[A-Z]/.test(s.slice(1)) ? s : s[0].toLowerCase() + s.slice(1));

/** `a` is "you" in the signed-in view and a name in the lookup view. */
function phraseBetween(e, a, b) {
  const q = e.q, first = lc(q.options[e.a.choice]), second = lc(q.options[e.b.choice]);
  const topic = q.prompt.replace(/[:.?]$/, '');
  const are = a === 'you' ? "you're" : `${a} is`;
  const said = a === 'you' ? 'you said' : `${a} said`;
  if (q.kind === 'complement' && e.a.choice !== e.b.choice) return `${topic} — ${are} ${first}, ${b} is ${second}`;
  if (e.a.choice === e.b.choice) return `${topic}: ${first}`;
  return `${topic} — ${said} ${first}, ${b} said ${second}`;
}

// ---------- anyone's top match ----------
const shortName = (p) => (p.seed ? p.name.split(' ')[0] : p.displayName ? p.displayName.split(' ')[0] : '@' + p.handle);

function sideHTML(p) {
  const inner = `${avatarHTML(p, 'md')}<span class="nm">${esc(label(p))}</span><span class="sub">${esc(p.seed ? p.group || '' : '@' + p.handle)}</span>`;
  return `<div class="side">${p.seed ? inner : `<a href="https://bsky.app/profile/${esc(p.handle)}" target="_blank" rel="noopener">${inner}</a>`}</div>`;
}

function duoHTML(d) {
  const { agree, clash } = explain(d.person.answers || {}, d.best.answers || {});
  const a = shortName(d.person), b = shortName(d.best);
  const bits = [];
  agree.slice(0, 2).forEach((e) => bits.push(`<b>Both:</b> ${esc(phraseBetween(e, a, b))}.`));
  if (clash[0] && d.best.score < 0.999) bits.push(`<span class="clash"><b>But:</b> ${esc(phraseBetween(clash[0], a, b))}.</span>`);
  const guess = [d.person, d.best, d.nemesis].some((p) => p && p.seed && p.realPerson) ? ' · ringer answers are our best guess' : '';
  const nem = d.nemesis ? `<div class="nem">Nemesis: ${esc(label(d.nemesis))} · ${pct(d.nemesis.score)}${esc(guess)}</div>` : '';
  return `<div class="duo"><div class="faces">${sideHTML(d.person)}<div class="score">${pct(d.best.score)}</div>${sideHTML(d.best)}</div>
    <div class="why2">${bits.join(' ') || 'Not much overlap, but the numbers say yes.'}</div>${nem}</div>`;
}

async function runLookup(inputId, slotId) {
  const el = $(inputId), slot = $(slotId);
  const key = el.value.trim().replace(/^@/, '').toLowerCase();
  if (!key) return;
  slot.innerHTML = '<div class="note">Looking…</div>';
  try {
    const d = await api(`/api/top/${encodeURIComponent(key)}`);
    slot.innerHTML = duoHTML(d);
  } catch (err) { slot.innerHTML = `<div class="note err">${esc(err.message)}</div>`; }
}
$('lookupForm').onsubmit = (e) => { e.preventDefault(); runLookup('lookup', 'lookupSlot'); };
$('lookupForm2').onsubmit = (e) => { e.preventDefault(); runLookup('lookup2', 'lookupSlot2'); };
// Real people get their Bluesky avatar; ringers get a coloured monogram, so the hero is
// never a bare percentage with no face next to it.
function avatarHTML(p, size) {
  const cls = 'av' + (size === true || size === 'lg' ? ' av-lg' : size === 'md' ? ' av-md' : '');
  if (p.avatar) return `<img class="${cls}" src="${esc(p.avatar)}" alt="" loading="lazy">`;
  const source = p.seed ? p.name : p.displayName || p.handle;
  const initials = String(source).split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  return `<span class="${cls} mono" style="--h:${seedOf(p.did || p.handle) % 360}">${esc(initials)}</span>`;
}

function heroHTML(p, kind) {
  const { agree, clash } = explain(me.answers, p.answers);
  const who = p.seed ? p.name.split(' ')[0] : 'they';
  const bits = [];
  if (kind === 'best') {
    agree.slice(0, 2).forEach((e) => bits.push(`<b>Agreed:</b> ${esc(phrase(e, who))}.`));
    // a perfect score shouldn't be followed by "but you disagree about..."
    if (clash[0] && p.score < 0.999) bits.push(`<span class="clash"><b>But:</b> ${esc(phrase(clash[0], who))}.</span>`);
  } else {
    clash.slice(0, 2).forEach((e) => bits.push(`<span class="clash"><b>Clash:</b> ${esc(phrase(e, who))}.</span>`));
    if (agree[0]) bits.push(`<b>Somehow:</b> ${esc(phrase(agree[0], who))}.`);
  }
  const guess = p.seed && p.realPerson ? ' · our best guess at their answers' : '';
  const sub = p.seed ? `${esc(p.group)}${guess}`
    : [`<a href="https://bsky.app/profile/${esc(p.handle)}" target="_blank" rel="noopener">@${esc(p.handle)}</a>`, REL_LABEL[p.rel] || '', p.followers != null ? `${p.followers.toLocaleString()} followers` : ''].filter(Boolean).join(' · ');
  const link = p.seed ? '' : ` href="https://bsky.app/profile/${esc(p.handle)}" target="_blank" rel="noopener"`;
  const face = p.seed ? avatarHTML(p, true) : `<a class="face"${link}>${avatarHTML(p, true)}</a>`;
  return `<div class="hero"><div class="pct">${pct(p.score)}</div><div class="whoblock">${face}<div><div class="who display">${esc(label(p))}</div><div class="group">${sub}</div></div></div><div class="why">${bits.join(' ')}</div></div>`;
}
const REL_LABEL = { mutual: 'mutuals', following: 'you follow', followed_by: 'follows you', none: '' };
function nameHTML(p) {
  if (p.seed) return `<span class="n">${avatarHTML(p)}${esc(label(p))}</span><span class="g">${esc(p.group || '')}</span>`;
  const av = avatarHTML(p);
  const name = p.displayName ? `${esc(p.displayName)} <span class="g">@${esc(p.handle)}</span>` : `@${esc(p.handle)}`;
  const rel = p.rel && p.rel !== 'none' ? `<span class="rel ${p.rel}">${REL_LABEL[p.rel]}</span>` : '';
  const fol = p.followers != null ? `<span class="g">${p.followers.toLocaleString()} followers</span>` : '';
  return `<a class="n" href="https://bsky.app/profile/${esc(p.handle)}" target="_blank" rel="noopener">${av}${name}</a>${rel}${fol}`;
}
function rowHTML(p, bad) {
  const { agree, clash } = explain(me.answers, p.answers);
  const who = p.seed ? p.name.split(' ')[0] : 'they';
  const e = bad ? clash[0] : agree[0];
  const why = e ? (bad ? `<span class="clash">${esc(phrase(e, who))}</span>` : esc(phrase(e, who))) : '';
  return `<div class="row${bad ? ' bad' : ''}"><div class="p">${pct(p.score)}</div><div>${nameHTML(p)}</div><div class="w">${why}</div></div>`;
}
function renderResults() {
  const r = lastResults;
  me.answers = r.me.answers; me.handle = r.me.handle; saveLocal(); renderWho();
  $('youline').textContent = `Results for @${me.handle} · ${Object.keys(me.answers).length} answered`;
  $('poolsize').textContent = `${r.poolSize} in the pool`;
  if (!r.best) { $('heroSlot').innerHTML = `<div class="note">Nobody to compare with yet.</div>`; $('people').innerHTML = ''; $('ringers').innerHTML = ''; $('bottom').innerHTML = ''; return; }
  $('heroSlot').innerHTML = heroHTML(r.best, 'best');
  const ppl = r.people.top.filter((p) => p.did !== r.best.did);
  const kn = r.people.known ?? 0, mu = r.people.mutuals ?? 0;
  const bits = [`${r.people.count} taken it`];
  if (kn) bits.push(mu ? `${kn} you're connected to (${mu} mutual)` : `${kn} you're connected to`);
  if (r.people.unchecked) bits.push(`${r.people.unchecked} not checked`);
  $('peoplecount').textContent = r.people.count ? bits.join(' · ') : '';
  $('people').innerHTML = ppl.length ? ppl.map((p) => rowHTML(p, false)).join('')
    : `<div class="empty">${r.people.count ? 'Your top match is the only other person so far.' : 'Nobody you know has taken it yet — send them the link and this fills in.'}</div>`;
  $('ringercount').textContent = `${r.ringers.count} in the bank`;
  $('ringers').innerHTML = r.ringers.top.filter((p) => p.did !== r.best.did).map((p) => rowHTML(p, false)).join('');
  $('bottom').innerHTML = r.nemesis ? rowHTML(r.nemesis, true) : '';
  $('seeAll').textContent = `See all ${(r.all || []).length} matches, ranked`;
}

// ---------- all matches ----------
let allFilter = 'all';
const allRows = () => (lastResults?.all || []).filter((p) =>
  allFilter === 'all' ? true : allFilter === 'people' ? !p.seed : p.seed);

function allRowHTML(p, rank, total) {
  // the relationship gets a badge of its own, so keep it out of the subtitle
  const sub = p.seed ? esc(p.group || '') : p.displayName ? '@' + esc(p.handle) : '';
  const rel = !p.seed && p.rel && p.rel !== 'none' ? `<span class="rel ${p.rel}">${REL_LABEL[p.rel]}</span>` : '';
  return `<button class="arow${rank === total ? ' lo' : ''}" type="button" data-did="${esc(p.did)}">
    <span class="rank">${rank}</span><span class="p">${pct(p.score)}</span>
    <span class="who2">${avatarHTML(p)}<span class="aname">${esc(label(p))}</span><span class="g">${sub}</span>${rel}</span>
  </button><div class="awhy hidden" data-why="${esc(p.did)}"></div>`;
}

function renderAll() {
  const rows = allRows(), total = (lastResults?.all || []).length;
  const people = (lastResults?.all || []).filter((p) => !p.seed).length;
  $('allCount').textContent = `${rows.length} shown · ${total} in the pool`;
  $('allTitle').textContent = allFilter === 'people' ? 'People on Bluesky' : allFilter === 'ringers' ? 'Ringers' : 'Everyone';
  document.querySelectorAll('#allTabs .tab').forEach((t) => t.classList.toggle('on', t.dataset.filter === allFilter));
  $('allTabs').querySelector('[data-filter="people"]').textContent = `People (${people})`;
  $('allTabs').querySelector('[data-filter="ringers"]').textContent = `Ringers (${total - people})`;
  $('allList').innerHTML = rows.length
    ? rows.map((p, i) => allRowHTML(p, i + 1, rows.length)).join('')
    : '<div class="empty">Nobody here yet.</div>';
}

$('allTabs').onclick = (e) => { const t = e.target.closest('.tab'); if (!t) return; allFilter = t.dataset.filter; renderAll(); };
$('seeAll').onclick = () => { allFilter = 'all'; renderAll(); show('all'); };
$('backToResults').onclick = () => show('results');

// Expanding a row fetches that pair, since the list itself ships without answers.
$('allList').onclick = async (e) => {
  const row = e.target.closest('.arow'); if (!row) return;
  const why = row.nextElementSibling;
  if (!why.classList.contains('hidden')) { why.classList.add('hidden'); return; }
  document.querySelectorAll('.awhy').forEach((el) => el.classList.add('hidden'));
  why.classList.remove('hidden');
  if (why.dataset.loaded) return;
  why.textContent = 'Looking…';
  try {
    const d = await api(`/api/pair/${encodeURIComponent(me.did)}/${encodeURIComponent(row.dataset.did)}`);
    const { agree, clash } = explain(me.answers, d.b.answers || {});
    const who = d.b.seed ? d.b.name.split(' ')[0] : 'they';
    const bits = [];
    agree.slice(0, 2).forEach((x) => bits.push(`<b>Agreed:</b> ${esc(phrase(x, who))}.`));
    if (clash[0] && (d.score ?? 0) < 0.999) bits.push(`<span class="clash"><b>But:</b> ${esc(phrase(clash[0], who))}.</span>`);
    why.innerHTML = bits.join(' ') || 'Not enough overlap to say much.';
    why.dataset.loaded = '1';
  } catch (err) { why.textContent = `Couldn't load that one (${err.message}).`; }
};

$('cmpForm').onsubmit = async (e) => {
  e.preventDefault();
  const h = $('cmp').value.trim().replace(/^@/, '').toLowerCase();
  if (!h) return;
  try {
    const d = await api(`/api/pair/${encodeURIComponent(me.did)}/${encodeURIComponent(h)}`);
    if (d.score == null) { $('cmpSlot').innerHTML = `<div class="note">You and ${esc(label(d.b))} haven't answered enough of the same questions to score.</div>`; return; }
    $('cmpSlot').innerHTML = heroHTML({ ...d.b, score: d.score }, d.score >= 0.5 ? 'best' : 'worst');
  } catch (err) { $('cmpSlot').innerHTML = `<div class="note err">${esc(err.message)}</div>`; }
};
$('copy').onclick = async () => {
  const r = lastResults; if (!r?.best) return;
  const best = r.best, worst = r.nemesis || r.best;
  const txt = `Skymate says I'm ${pct(best.score)} compatible with ${label(best)} and ${pct(worst.score)} with ${label(worst)}. Find yours: ${location.origin}`;
  try { await navigator.clipboard.writeText(txt); $('copy').textContent = 'Copied'; setTimeout(() => ($('copy').textContent = 'Copy my result'), 1500); } catch { prompt('Copy this:', txt); }
};

boot();
